import React from 'react';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { CallHistoryMethodAction, push } from 'connected-react-router';

import { AnimatePresence, motion } from 'framer-motion';

import AppState from '@webapp/store/state';

import { Category } from '@common/types/categories/category';
import { ReleaseStore } from '@common/types/release/release-store';

import StoreSelectorCtrl from './store-selector';
import StoreDropdownList from './store-dropdown-list';

import CatalogReleaseInfoSidebar from '../../release-info-sidebar';

import ButtonLoadingCtrl from '@webapp/controllers/button-loading';

import Box from '@components/layout/box';
import Flex from '@components/layout/flex';
import Grid from '@components/basics/grid';
import Hint from '@components/basics/hint';
import { getWidth } from '@components/styles';
import Button from '@components/actions/button';
import { easeTransition } from '@components/framer';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import UnselectedIcon from '@components/basics/icons/unselected';
import BoxTitleHeader from '@components/layout/box-title-header';
import CompletedIcon from '@components/basics/icons/achievements-icons/completed';
import { TakedownRelease } from '@webapp/store/release/action-creators';

interface TakedownSubmitProps {
  className?: string;
  setSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  releaseTitle: string;
  releaseCover: string;
  artistId: string;
  releaseId: string;
  isNFT?: boolean;
  tracks: {
    number: number;
    name: string;
  }[];
  activeStore: ReleaseStore[];
  stores: Category<ReleaseStore>[];
  takeDownRelease: (
    artistId: string,
    releasesId: string,
    releasetitle: string,
    takedownBody: any
  ) => TakedownRelease;
  setActiveStore: React.Dispatch<React.SetStateAction<ReleaseStore[]>>;
  push: (path: string) => CallHistoryMethodAction;
}

const mapStateToProps = (state: AppState) => ({
  stores: state.categories.lists['platform'] || [],
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  push: (path: string) => dispatch(push(path)),
  takeDownRelease: (
    artistId: string,
    releasesId: string,
    releaseTitle: string,
    takedownBody: any
  ) =>
    dispatch(
      new TakedownRelease(artistId, releasesId, releaseTitle, takedownBody)
    ),
});

export const TakedownSubmit = ({
  setSubmitted,
  className,
  releaseTitle,
  releaseCover,
  artistId,
  takeDownRelease,
  releaseId,
  isNFT,
  tracks,
  stores,
  activeStore,
  setActiveStore,
  push,
}: TakedownSubmitProps) => {
  const [selected, setSelected] = React.useState<number>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [confirmed, setConfirmed] = React.useState<boolean>(false);

  const hoverMotion = {
    rest: { scale: 1 },
    hover: { scale: 1.015, transition: { ...easeTransition } },
  };

  React.useEffect(() => {
    setActiveStore(stores.map((store) => store.metadata));
  }, [stores, setActiveStore]);

  const handleSubmit = () => {
    setIsLoading(true);
    let takedownBody = {
      releaseId: releaseId,
      stores: activeStore.map((store) => {
        return { id: store.id, name: store.name };
      }),
    };
    takeDownRelease(artistId, releaseId, releaseTitle, takedownBody);
    setIsLoading(false);
    setSubmitted(true);
  };

  const handleCheckClick = (
    ev: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    value: number
  ) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (selected !== value) {
      setSelected(value);
    } else {
      return setSelected(undefined);
    }
  };

  return (
    <MotionOpacity className={className}>
      <TakeDownWrapper>
        <CatalogReleaseInfoSidebar
          releaseTitle={releaseTitle}
          releaseCover={releaseCover}
          isNFT={isNFT}
          nftType="Music"
          tracks={tracks}
        />

        <Header gap={10}>
          <Typography customFont="semi-bold" type="subheader">
            <Translate id="CATALOG.TAKEDOWN.TITLE" />
          </Typography>
        </Header>

        {/* TODO: DEVTEAM-603 - This box needs to be integrated to takedawn a nft */}
        {isNFT ? (
          <Box noBorderBottom>
            <BoxTitleHeader
              className="mb-20"
              title={<Translate id="CATALOG.TAKEDOWN.BOX.TITLE" />}
            />
            <CustomCheck
              className={selected === 3 ? 'selected' : ''}
              whileHover={selected !== 3 ? 'hover' : 'rest'}
              initial="rest"
              variants={hoverMotion}
              onClick={(ev) => handleCheckClick(ev, 3)}
            >
              <Flex justifyContent="space-between" alignItems="center">
                <Typography weight="700">
                  <Translate id="CATALOG.TAKEDOWN.BOX.NFT" />
                </Typography>

                <CheckContainer>
                  {selected === 3 ? (
                    <CompletedIcon className="icon" />
                  ) : (
                    <UnselectedIcon className="icon" />
                  )}
                </CheckContainer>
              </Flex>
            </CustomCheck>
          </Box>
        ) : confirmed ? (
          <StoreDropdownList
            stores={stores.map((store) => store.metadata)}
            activeStore={activeStore}
            noBorderBottom
          />
        ) : (
          <Box noBorderBottom>
            <BoxTitleHeader
              className="mb-20"
              title={<Translate id="CATALOG.TAKEDOWN.BOX.TITLE" />}
            />
            <Grid gap={10}>
              <CustomCheck
                className={selected === 0 ? 'selected' : ''}
                whileHover={selected !== 0 ? 'hover' : 'rest'}
                initial="rest"
                variants={hoverMotion}
                onClick={(ev) => handleCheckClick(ev, 0)}
              >
                <Flex justifyContent="space-between" alignItems="center">
                  <Typography weight="700">
                    <Translate id="CATALOG.TAKEDOWN.BOX.ALL_STORES" />
                  </Typography>

                  <CheckContainer>
                    {selected === 0 ? (
                      <CompletedIcon className="icon" />
                    ) : (
                      <UnselectedIcon className="icon" />
                    )}
                  </CheckContainer>
                </Flex>
              </CustomCheck>

              <div>
                <CustomCheck
                  className={selected === 1 ? 'selected no-border-bottom' : ''}
                  whileHover={selected !== 1 ? 'hover' : 'rest'}
                  initial="rest"
                  variants={hoverMotion}
                  onClick={() => setSelected(selected === 1 ? undefined : 1)}
                >
                  <Flex justifyContent="space-between" alignItems="center">
                    <Typography weight="700">
                      <Translate id="CATALOG.TAKEDOWN.BOX.SPECIFIC_STORES" />
                    </Typography>

                    <CheckContainer>
                      {selected === 1 ? (
                        <CompletedIcon className="icon" />
                      ) : (
                        <UnselectedIcon className="icon" />
                      )}
                    </CheckContainer>
                  </Flex>
                </CustomCheck>
                <AnimatePresence exitBeforeEnter>
                  {selected === 1 && (
                    <CustomCheckContainer>
                      <StoreSelectorCtrl
                        className="store-selector"
                        isDarker
                        setActiveStore={setActiveStore}
                        activeStore={activeStore}
                      />
                    </CustomCheckContainer>
                  )}
                </AnimatePresence>
              </div>
            </Grid>
          </Box>
        )}
        <Hint
          noBorderTop
          customTitle={
            <Translate
              id={
                confirmed
                  ? 'CATALOG.TAKEDOWN.BOX.HINT.CONFIRMED.TITLE'
                  : 'CATALOG.TAKEDOWN.BOX.HINT.TITLE'
              }
            />
          }
        >
          <Translate
            id={
              confirmed
                ? 'CATALOG.TAKEDOWN.BOX.HINT.CONFIRMED.TEXT'
                : 'CATALOG.TAKEDOWN.BOX.HINT.TEXT'
            }
          />
        </Hint>

        <ButtonWrapper>
          <Button
            className="btn"
            buttonType="secondary"
            onClick={() => push('/catalog')}
          >
            <Translate id="CATALOG.TAKEDOWN.BUTTON.CANCEL" />
          </Button>
          <ButtonLoadingCtrl
            className="btn"
            isLoading={isLoading}
            disabled={!(typeof selected === 'number')}
            onClick={() => (confirmed ? handleSubmit() : setConfirmed(true))}
          >
            <Translate id="CATALOG.TAKEDOWN.BUTTON.TAKEDOWN" />
          </ButtonLoadingCtrl>
        </ButtonWrapper>
      </TakeDownWrapper>
    </MotionOpacity>
  );
};
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(TakedownSubmit)`
  position: relative;
  margin-top: 80px;
`);

const TakeDownWrapper = styled.div`
  min-height: 100vh;
  padding-inline: 24px;
  max-width: ${getWidth(10)};
  margin-inline: auto;

  @media ${({ theme }) => theme.device.laptopS} {
    width: auto;
    padding-inline: 0;
  }

  .title-header {
    margin-bottom: 20px;
  }
`;

const ButtonWrapper = styled.div`
  margin-top: 40px;
  gap: 5px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .btn {
    width: 100%;
    flex-grow: 1;
  }
`;

const Header = styled(Grid)`
  margin-bottom: 30px;
`;

const CustomCheck = styled(motion.a)`
  display: block;
  cursor: pointer;
  padding: 20px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.differentBackground};
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  span {
    transition: color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
  }

  &.selected {
    background-color: ${({ theme }) => theme.colors.primary};

    span {
      color: ${({ theme }) => theme.colors.differentBackground};
    }
  }

  &.no-border-bottom {
    border-radius: 4px 4px 0 0;
  }
`;

const CustomCheckContainer = styled(MotionOpacity)`
  padding: 20px;
  padding-top: 0;
  border-radius: 0 0 4px 4px;
  background-color: ${({ theme }) => theme.colors.primary};
`;

const CheckContainer = styled.div`
  height: 32px;
  width: 32px;
  flex-shrink: 0;
  .icon {
    width: 32px;
    height: 32px;
  }
`;
